import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useDataProvider, useNotify, type Identifier } from 'react-admin';

import {
    BILLING_PLAN_FEATURES_RESOURCE,
    type BillingCountryCurrency,
    type BillingFeature,
    type BillingPlan,
    type BillingPlanFeature,
    getBillingFeatureLabel,
} from './billingData';
import { formatUpdatedAt, getStartingPlanPrice } from './billingUtils';

type EntitlementCellDraft = {
    included: boolean;
    value: string;
};

type EntitlementDrafts = Record<string, EntitlementCellDraft>;

type BillingEntitlementsSectionProps = {
    countryCurrencies: BillingCountryCurrency[];
    features: BillingFeature[];
    planFeatures: BillingPlanFeature[];
    plans: BillingPlan[];
    onChanged: () => Promise<void>;
};

function getCellKey(planId: Identifier, featureId: Identifier): string {
    return `${String(planId)}::${String(featureId)}`;
}

function getDefaultFeatureValue(feature: BillingFeature): string {
    if (feature.dataType === 1) return 'true';
    return '';
}

function createPlanFeatureLookup(planFeatures: BillingPlanFeature[]) {
    return new Map(planFeatures.map((item) => [getCellKey(item.planId, item.featureId), item]));
}

function createEntitlementDrafts(
    plans: BillingPlan[],
    features: BillingFeature[],
    planFeatures: BillingPlanFeature[],
): EntitlementDrafts {
    const existingByCell = createPlanFeatureLookup(planFeatures);
    const drafts: EntitlementDrafts = {};

    for (const plan of plans) {
        for (const feature of features) {
            const key = getCellKey(plan.id, feature.id);
            const existing = existingByCell.get(key);

            drafts[key] = {
                included: Boolean(existing),
                value: existing?.value ?? getDefaultFeatureValue(feature),
            };
        }
    }

    return drafts;
}

function getSavedCellValue(feature: BillingFeature, draft: EntitlementCellDraft): string {
    if (feature.dataType === 1) return draft.included ? 'true' : '';
    return draft.value.trim();
}

function isChangedCell(
    feature: BillingFeature,
    draft: EntitlementCellDraft | undefined,
    existing: BillingPlanFeature | undefined,
): boolean {
    if (!draft) return false;
    if (draft.included !== Boolean(existing)) return true;
    if (!draft.included || !existing) return false;
    return getSavedCellValue(feature, draft) !== existing.value;
}

export function BillingEntitlementsSection({
    countryCurrencies,
    features,
    planFeatures,
    plans,
    onChanged,
}: BillingEntitlementsSectionProps) {
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const activePlans = useMemo(() => plans.filter((plan) => plan.isActive), [plans]);
    const activePlanIds = useMemo(() => new Set(activePlans.map((plan) => plan.id)), [activePlans]);
    const activePlanFeatures = useMemo(
        () => planFeatures.filter((item) => activePlanIds.has(item.planId)),
        [activePlanIds, planFeatures],
    );
    const existingByCell = useMemo(() => createPlanFeatureLookup(activePlanFeatures), [activePlanFeatures]);
    const [entitlementDrafts, setEntitlementDrafts] = useState<EntitlementDrafts>(() =>
        createEntitlementDrafts(activePlans, features, activePlanFeatures),
    );
    const [isSavingMatrix, setIsSavingMatrix] = useState(false);

    useEffect(() => {
        setEntitlementDrafts(createEntitlementDrafts(activePlans, features, activePlanFeatures));
    }, [activePlans, activePlanFeatures, features]);

    const changedCells = useMemo(
        () =>
            activePlans.flatMap((plan) =>
                features
                    .map((feature) => {
                        const key = getCellKey(plan.id, feature.id);
                        const draft = entitlementDrafts[key];
                        const existing = existingByCell.get(key);

                        if (!isChangedCell(feature, draft, existing)) {
                            return null;
                        }

                        return { draft: draft as EntitlementCellDraft, existing, feature, key, plan };
                    })
                    .filter((item): item is {
                        draft: EntitlementCellDraft;
                        existing: BillingPlanFeature | undefined;
                        feature: BillingFeature;
                        key: string;
                        plan: BillingPlan;
                    } => item !== null),
            ),
        [activePlans, entitlementDrafts, existingByCell, features],
    );

    const handleResetMatrix = () => {
        setEntitlementDrafts(createEntitlementDrafts(activePlans, features, activePlanFeatures));
    };

    const setCellIncluded = (planId: Identifier, feature: BillingFeature, included: boolean) => {
        const key = getCellKey(planId, feature.id);
        setEntitlementDrafts((current) => ({
            ...current,
            [key]: {
                included,
                value: current[key]?.value || getDefaultFeatureValue(feature),
            },
        }));
    };

    const setCellValue = (planId: Identifier, feature: BillingFeature, value: string) => {
        const key = getCellKey(planId, feature.id);
        setEntitlementDrafts((current) => ({
            ...current,
            [key]: {
                included: current[key]?.included ?? true,
                value,
            },
        }));
    };

    const handleSaveMatrix = async () => {
        const missingValue = changedCells.find(
            ({ draft, feature }) => draft.included && feature.dataType !== 1 && getSavedCellValue(feature, draft) === '',
        );

        if (missingValue) {
            notify(`${getBillingFeatureLabel(missingValue.feature)} needs an assigned value`, { type: 'warning' });
            return;
        }

        setIsSavingMatrix(true);
        try {
            for (const { draft, existing, feature, plan } of changedCells) {
                if (draft.included) {
                    const payload = {
                        planId: plan.id,
                        featureId: feature.id,
                        value: getSavedCellValue(feature, draft),
                    };

                    if (existing) {
                        await dataProvider.update(BILLING_PLAN_FEATURES_RESOURCE, {
                            id: existing.id,
                            data: payload,
                            previousData: existing,
                        });
                    } else {
                        await dataProvider.create(BILLING_PLAN_FEATURES_RESOURCE, { data: payload });
                    }
                    continue;
                }

                if (existing) {
                    await dataProvider.delete(BILLING_PLAN_FEATURES_RESOURCE, {
                        id: existing.id,
                        previousData: existing,
                    });
                }
            }

            notify('Entitlement matrix saved', { type: 'info' });
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save entitlement matrix';
            notify(message, { type: 'error' });
        } finally {
            setIsSavingMatrix(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Alert severity="info">
                Market pricing now lives on each plan. Use this matrix to choose which features each active plan
                includes and save the assigned values against the backend plan-feature records.
            </Alert>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {activePlans.map((plan) => (
                    <Paper key={plan.id} variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1" noWrap title={plan.name}>
                                {plan.name}
                            </Typography>
                            <Typography variant="h6">{getStartingPlanPrice(plan, countryCurrencies)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Renews every {plan.durationDays} days
                            </Typography>
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                <Stack spacing={0.5}>
                    <Typography variant="subtitle1">Feature Map</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Check a cell to include that feature in the plan. Numeric and text features also need the
                        saved value used by the subscription service.
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetMatrix}
                        disabled={changedCells.length === 0 || isSavingMatrix}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isSavingMatrix ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                        onClick={() => void handleSaveMatrix()}
                        disabled={changedCells.length === 0 || isSavingMatrix}
                    >
                        Save Changes
                    </Button>
                </Stack>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: Math.max(760, 260 + activePlans.length * 210) }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    bgcolor: 'background.paper',
                                    left: 0,
                                    minWidth: 260,
                                    position: 'sticky',
                                    zIndex: 2,
                                }}
                            >
                                Feature
                            </TableCell>
                            {activePlans.map((plan) => (
                                <TableCell key={plan.id} align="center" sx={{ minWidth: 210, verticalAlign: 'top' }}>
                                    <Stack spacing={0.5} alignItems="center">
                                        <Typography variant="subtitle2">{plan.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getStartingPlanPrice(plan, countryCurrencies)}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {features.map((feature) => (
                            <TableRow key={feature.id} hover>
                                <TableCell
                                    sx={{
                                        bgcolor: 'background.paper',
                                        left: 0,
                                        minWidth: 260,
                                        position: 'sticky',
                                        verticalAlign: 'top',
                                        zIndex: 1,
                                    }}
                                >
                                    <Stack spacing={0.75}>
                                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                            <Typography variant="subtitle2">{getBillingFeatureLabel(feature)}</Typography>
                                            <Chip
                                                size="small"
                                                label={feature.dataType === 1 ? 'Boolean' : feature.dataType === 2 ? 'Integer' : 'Text'}
                                            />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {feature.key}
                                        </Typography>
                                        {feature.description ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {feature.description}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </TableCell>
                                {activePlans.map((plan) => {
                                    const key = getCellKey(plan.id, feature.id);
                                    const draft = entitlementDrafts[key] ?? {
                                        included: false,
                                        value: getDefaultFeatureValue(feature),
                                    };
                                    const existing = existingByCell.get(key);
                                    const isDirty = isChangedCell(feature, draft, existing);

                                    return (
                                        <TableCell
                                            key={key}
                                            align="center"
                                            sx={{
                                                bgcolor: isDirty ? 'action.hover' : undefined,
                                                verticalAlign: 'top',
                                            }}
                                        >
                                            <Stack spacing={1} alignItems="center">
                                                <Tooltip title="Include this feature in the plan">
                                                    <Checkbox
                                                        checked={draft.included}
                                                        onChange={(event) =>
                                                            setCellIncluded(plan.id, feature, event.target.checked)
                                                        }
                                                        inputProps={{
                                                            'aria-label': `${plan.name} includes ${getBillingFeatureLabel(feature)}`,
                                                        }}
                                                    />
                                                </Tooltip>

                                                {feature.dataType === 1 ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {draft.included ? 'Included' : 'Not included'}
                                                    </Typography>
                                                ) : (
                                                    <TextField
                                                        size="small"
                                                        label="Value"
                                                        value={draft.value}
                                                        disabled={!draft.included}
                                                        placeholder={feature.dataType === 2 ? '100' : 'Unlimited'}
                                                        onChange={(event) => setCellValue(plan.id, feature, event.target.value)}
                                                        fullWidth
                                                    />
                                                )}

                                                {existing?.updatedAt ? (
                                                    <Box sx={{ minHeight: 18 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatUpdatedAt(existing.updatedAt)}
                                                        </Typography>
                                                    </Box>
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {activePlans.length === 0 || features.length === 0 ? (
                <Alert severity="warning">
                    Add at least one active plan and one feature before saving the feature map.
                </Alert>
            ) : null}
        </Stack>
    );
}
